import {
  FilterType,
  FunctionalGroupRule,
  QueryOperator,
  NumberRule,
  Query,
  TextRule,
  Rule,
  RULE_TYPE_QUERY,
  Filter,
} from '@moleculeone/types';

import { Button, Dropdown } from '../..';
import { Row } from './Row';
import { Operator } from './Operator';
import { Container } from './Container';
import { Body } from './Body';
import { TextRuleField } from './rules/TextRuleField';
import { NumberRuleField } from './rules/NumberRuleField';
import { FunctionalGroupRuleField } from './rules/FunctionalGroupRule/FunctionalGroupRuleField';

export interface QueryBuilderProps {
  filters: Filter[];
  value: Query;
  onChange: (nextValue: Query) => void;
  onRemove?: () => void;
}
const FILTER_RULE_CONSTRUCTORS: {
  [key in FilterType]: new () => TextRule | NumberRule | FunctionalGroupRule;
} = {
  [FilterType.TEXT]: TextRule,
  [FilterType.NUMBER]: NumberRule,
  [FilterType.FUNCTIONAL_GROUP]: FunctionalGroupRule,
};

const OPERATOR_ITEMS: { value: QueryOperator; label: string }[] = [
  { value: QueryOperator.AND, label: 'AND' },
  { value: QueryOperator.OR, label: 'OR' },
];

export function QueryBuilder(props: QueryBuilderProps) {
  const { filters, value, onChange, onRemove } = props;

  const { operator, rules } = value;

  function onOperatorChange(operator: QueryOperator) {
    if (!value) {
      return;
    }

    const nextValue = { ...value, operator };
    onChange(nextValue);
  }

  function onRuleValueChange<T extends Rule>(idx: number) {
    return (nextRuleValue: T) => {
      const nextRulesValue = [...value.rules?.slice(0, idx), nextRuleValue, ...value.rules?.slice(idx + 1)];
      onChange({ ...value, rules: nextRulesValue });
    };
  }

  function onRuleRemove(idx: number) {
    return () => {
      const nextFiltersValue = [...value.rules?.slice(0, idx), ...value.rules?.slice(idx + 1)];
      onChange({ ...value, rules: nextFiltersValue });
    };
  }

  function onRuleAdd<T extends Rule>(RuleConstructor: new () => T, ruleId?: string) {
    return () => {
      const newRule = new RuleConstructor();

      if (newRule.type !== RULE_TYPE_QUERY && ruleId) newRule.id = ruleId;

      const nextRulesValue = [...value.rules, newRule];

      onChange({ ...value, rules: nextRulesValue });
    };
  }

  return (
    <Container operator={operator}>
      <Operator items={OPERATOR_ITEMS} operator={operator} onChange={onOperatorChange} onRemove={onRemove} />

      <Body>
        {rules?.map((rule, idx) => {
          const type = rule.type;

          switch (type) {
            case FilterType.TEXT: {
              const { ruleFieldProps, name } = filters.find(r => r.id === rule.id) ?? {};

              return (
                <Row key={rule.key}>
                  <TextRuleField label={name} state={rule} onChange={onRuleValueChange(idx)} {...ruleFieldProps} />
                  <Button icon="delete" size="small" theme="red" weight="minimal" onClick={onRuleRemove(idx)} />
                </Row>
              );
            }
            case FilterType.NUMBER: {
              const { ruleFieldProps, name } = filters.find(r => r.id === rule.id) ?? {};

              return (
                <Row key={rule.key}>
                  <NumberRuleField label={name} state={rule} onChange={onRuleValueChange(idx)} {...ruleFieldProps} />
                  <Button icon="delete" size="small" theme="red" weight="minimal" onClick={onRuleRemove(idx)} />
                </Row>
              );
            }
            case FilterType.FUNCTIONAL_GROUP: {
              const { ruleFieldProps, name } = filters.find(r => r.id === rule.id) ?? {};

              return (
                <Row key={rule.key}>
                  <FunctionalGroupRuleField
                    label={name}
                    state={rule}
                    onChange={onRuleValueChange(idx)}
                    {...ruleFieldProps}
                  />
                  <Button icon="delete" size="small" theme="red" weight="minimal" onClick={onRuleRemove(idx)} />
                </Row>
              );
            }
            default: {
              return (
                <Row key={rule.key}>
                  <QueryBuilder
                    filters={filters}
                    value={rule}
                    onChange={onRuleValueChange(idx)}
                    onRemove={onRuleRemove(idx)}
                  />
                </Row>
              );
            }
          }
        })}

        <Row>
          <Dropdown.Root
            trigger={<Button text="Add rule" icon="add" size="small" weight="minimal" />}
            triggerProps={{ asChild: true }}
          >
            {filters?.map(filter => {
              const { id, type, name } = filter;

              return (
                <Dropdown.Item onClick={onRuleAdd(FILTER_RULE_CONSTRUCTORS[type], id)} key={id}>
                  {name}
                </Dropdown.Item>
              );
            })}

            <Dropdown.Separator />
            <Dropdown.Item onClick={onRuleAdd(Query)}>Add expression</Dropdown.Item>
          </Dropdown.Root>
        </Row>
      </Body>
    </Container>
  );
}
